import apiV2Service from 'common/src/modules/api/api-v2.service';
import _map from 'lodash/map';
import _partition from 'lodash/partition';
import {
  toClientDashboard,
  toClientDashboardWidget,
  toClientWidgetsOptions,
  toServerDashboardWidget,
} from './dashboards.dataConverters';
import {
  Dashboard,
  DashboardWidget,
  DashboardWidgetOption,
  DashboardWidgetType,
  RemoteDashboard,
  RemoteDashboardWidget,
  RemoteDashboardWidgetOption,
} from './dashboards.types';

const BASE_PATH = 'Dashboards';

async function getDashboards(): Promise<Dashboard[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDashboard[]>(`${BASE_PATH}`);

  return _map(data, toClientDashboard);
}

async function createDashboard(): Promise<number> {
  const {data} = await apiV2Service.executePostRequest<number>(`${BASE_PATH}`);

  return data;
}

async function getDashboard(dashboardId: number): Promise<Dashboard> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDashboard>(
    `${BASE_PATH}/${dashboardId}`,
  );

  return toClientDashboard(data);
}

async function getDefaultDashboard(): Promise<Dashboard> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDashboard>(`${BASE_PATH}/default`);

  return toClientDashboard(data);
}

async function deleteDashboard(dashboardId: number): Promise<number> {
  const {data} = await apiV2Service.executeDeleteRequest<number>(`${BASE_PATH}/${dashboardId}`);

  return data;
}

async function setAsDefaultDashboard(dashboardId: number): Promise<number> {
  const {data} = await apiV2Service.executePutRequest<number>(
    `${BASE_PATH}/default/${dashboardId}`,
  );

  return data;
}

async function updateDashboard(dashboard: Dashboard): Promise<number> {
  const {data} = await apiV2Service.executePutRequest<number>(
    `${BASE_PATH}/${dashboard.id}`,
    {},
    {...dashboard},
  );

  return data;
}

async function getWidgets(dashboardId: number): Promise<DashboardWidget[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDashboardWidget[]>(
    `${BASE_PATH}/${dashboardId}/widgets`,
  );

  const [childWidgets, widgets] = _partition(data, ({type}) => type === DashboardWidgetType.TABLE);

  return _map(widgets, (widget) => toClientDashboardWidget(widget, childWidgets));
}

async function createWidget(widget: DashboardWidget): Promise<number> {
  const params = toServerDashboardWidget(widget);
  const {data} = await apiV2Service.executePostRequest<number>(
    `${BASE_PATH}/widgets`,
    {},
    {
      ...params,
    },
  );

  return data;
}

async function editWidget(widget: DashboardWidget): Promise<number> {
  const params = toServerDashboardWidget(widget);
  const {data} = await apiV2Service.executePutRequest<number>(
    `${BASE_PATH}/widgets/${widget.id}`,
    {},
    {
      ...params,
    },
  );

  return data;
}

async function deleteWidget(widgetId: number): Promise<number> {
  const {data} = await apiV2Service.executeDeleteRequest<number>(
    `${BASE_PATH}/widgets/${widgetId}`,
  );

  return data;
}

async function updateAllWidgets(widgets: DashboardWidget[]) {
  const allWidgets = _map(widgets, (widget) => {
    const params = toServerDashboardWidget(widget);

    return apiV2Service.executePutRequest<number>(
      `${BASE_PATH}/widgets/${widget.id}`,
      {},
      {
        ...params,
      },
    );
  });

  await Promise.all([...allWidgets]);
}

async function getWidgetsOptions(): Promise<Record<DashboardWidgetType, DashboardWidgetOption>> {
  const {data} = await apiV2Service.executeGetRequest<
    Record<DashboardWidgetType, RemoteDashboardWidgetOption>
  >(`${BASE_PATH}/widgets/options`);

  return toClientWidgetsOptions(data);
}

async function duplicateDashboard(dashboardId: number): Promise<number> {
  const {data} = await apiV2Service.executePostRequest<number>(
    `${BASE_PATH}/duplicate/${dashboardId}`,
  );

  return data;
}

export default {
  getDashboard,
  getDashboards,
  createDashboard,
  deleteDashboard,
  updateDashboard,
  getWidgets,
  setAsDefaultDashboard,
  createWidget,
  deleteWidget,
  getDefaultDashboard,
  editWidget,
  updateAllWidgets,
  getWidgetsOptions,
  duplicateDashboard,
};
